import React, { useEffect, useState } from "react";
import "./Analytics.css";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api";
import { cloudApi } from "../../api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { FaPills, FaBoxes, FaWarehouse, FaExclamationTriangle, FaClock } from "react-icons/fa";

const COLORS = ["#4F46E5", "#22C55E", "#F59E0B"];

const Analytics = () => {
  const [data, setData] = useState(null);
  const [branchAnalytics, setBranchAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      // ── Fetch both endpoints in parallel ──
      // api   → reads local SQLite on desktop, cloud on web (pharmacy overview)
      // cloudApi → always hits cloud (branch analytics)
      const [overviewRes, branchRes] = await Promise.allSettled([
        api.get("/pharmacies/pharmacy-overview"),
        cloudApi.get("/pharmacies/pharmacies/branch-analytics"),
      ]);

      if (overviewRes.status === "fulfilled") {
        setData(overviewRes.value.data);
      } else {
        console.error("Overview fetch failed:", overviewRes.reason);
        setError("❌ Failed to load pharmacy overview.");
      }

      if (branchRes.status === "fulfilled") {
        setBranchAnalytics(branchRes.value.data);
      } else {
        console.error("Branch analytics fetch failed:", branchRes.reason);
        // Non-fatal — branch analytics section just won't render
      }

      setError("");
    } catch (err) {
      console.error("Analytics fetch failed:", err);
      setError("❌ Failed to load analytics data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // ── Full-page loading screen ──
  if (loading) {
    return (
      <div className="analytics-layout">
        <Sidebar />
        <div
          className="analytics-container"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            gap: "1rem",
          }}
        >
          <div
            style={{
              width: "44px",
              height: "44px",
              border: "4px solid #f0f0f0",
              borderTop: "4px solid #4F46E5",
              borderRadius: "50%",
              animation: "spin 0.9s linear infinite",
            }}
          />
          <p style={{ color: "#555", fontSize: "1rem", margin: 0, fontWeight: "500" }}>
            Loading analytics...
          </p>
          <p style={{ color: "#999", fontSize: "0.85rem", margin: 0 }}>
            Fetching your pharmacy data
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="analytics-layout">
        <Sidebar />
        <div className="analytics-container">
          <h2>📊 Pharmacy Analytics</h2>
          <div className="error-box">{error || "No data available"}</div>
        </div>
      </div>
    );
  }

  const overview = data.overview || {};
  const pharmacy = data.pharmacy;
  const branch = data.branch;

  const barData = [
    { name: "Unique Drugs", value: overview.total_unique_drugs || 0 },
    { name: "Inventory Items", value: overview.total_inventory_items || 0 },
    { name: "Total Quantity", value: overview.total_quantity || 0 },
  ];

  const pieData = barData.map((item, index) => ({
    ...item,
    fill: COLORS[index % COLORS.length],
  }));

  return (
    <div className="analytics-layout">
      <Sidebar />
      <div className="analytics-container">
        <h2>📊 Analytics Overview</h2>
        {error && <div className="error-box">{error}</div>}

        {/* ── Info Header ── */}
        <div className="info-card">
          {pharmacy ? (
            <h3>
              🏥 {pharmacy.pharmacy_name} — {pharmacy.total_branches} Branch
              {pharmacy.total_branches !== 1 && "es"}
            </h3>
          ) : branch ? (
            <h3>🏬 {branch.branch_name} ({branch.verification_status})</h3>
          ) : (
            <p>No pharmacy or branch information available.</p>
          )}
        </div>

        {/* ── Summary Cards ── */}
        <div className="cards-grid">
          <div className="stat-card purple">
            <FaPills className="card-icon" />
            <h3>{overview.total_unique_drugs}</h3>
            <p>Unique Drugs</p>
          </div>
          <div className="stat-card green">
            <FaBoxes className="card-icon" />
            <h3>{overview.total_inventory_items}</h3>
            <p>Inventory Items</p>
          </div>
          <div className="stat-card orange">
            <FaWarehouse className="card-icon" />
            <h3>{overview.total_quantity}</h3>
            <p>Total Quantity</p>
          </div>
        </div>

        {/* ── Charts Section ── */}
        <div className="charts-grid">
          <div className="chart-card card">
            <h3>📦 Inventory Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#4F46E5" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card card">
            <h3>🥧 Inventory Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Branch Analytics Section ── */}
        {branchAnalytics && branchAnalytics.branches?.length > 0 && (
          <div style={{ marginTop: "2rem" }}>
            <h2 style={{ marginBottom: "1.25rem" }}>🏬 Branch Analytics</h2>

            {branchAnalytics.branches.map((b) => {
              const stockSummary = b.stock_summary || {};

              // ── Bar chart data per branch ──
              const branchBarData = [
                { name: "Drug Entries", value: stockSummary.total_drug_entries || 0 },
                { name: "Unique Drugs", value: stockSummary.unique_generic_drugs || 0 },
                { name: "Total Qty", value: stockSummary.total_quantity || 0 },
              ];

              return (
                <div
                  key={b.branch_id}
                  style={{
                    background: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    padding: "1.5rem",
                    marginBottom: "1.5rem",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Branch header */}
                  <h3 style={{ marginTop: 0, marginBottom: "1rem", color: "#1f2937" }}>
                    🏬 {b.branch_name}
                  </h3>

                  {/* Stock summary cards */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                      gap: "1rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <div style={{ background: "#ede9fe", borderRadius: "8px", padding: "1rem", textAlign: "center" }}>
                      <p style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#4F46E5" }}>
                        {stockSummary.total_drug_entries ?? "—"}
                      </p>
                      <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "#6b7280" }}>Drug Entries</p>
                    </div>
                    <div style={{ background: "#dcfce7", borderRadius: "8px", padding: "1rem", textAlign: "center" }}>
                      <p style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#16a34a" }}>
                        {stockSummary.unique_generic_drugs ?? "—"}
                      </p>
                      <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "#6b7280" }}>Unique Drugs</p>
                    </div>
                    <div style={{ background: "#fef9c3", borderRadius: "8px", padding: "1rem", textAlign: "center" }}>
                      <p style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#ca8a04" }}>
                        {stockSummary.total_quantity ?? "—"}
                      </p>
                      <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "#6b7280" }}>Total Quantity</p>
                    </div>
                  </div>

                  {/* Branch stock bar chart */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4 style={{ marginBottom: "0.75rem", color: "#374151" }}>📊 Stock Overview</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={branchBarData}>
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#4F46E5" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Top drug */}
                  {b.top_drug && (
                    <div style={{ marginBottom: "1.25rem" }}>
                      <h4 style={{ marginBottom: "0.5rem", color: "#374151" }}>🏆 Top Drug by Quantity</h4>
                      <div style={{
                        background: "#f0f9ff",
                        border: "1px solid #bae6fd",
                        borderRadius: "8px",
                        padding: "0.75rem 1rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}>
                        <div>
                          <p style={{ margin: 0, fontWeight: 600, color: "#0369a1" }}>
                            {b.top_drug.generic_name}
                          </p>
                          {b.top_drug.brand_name && (
                            <p style={{ margin: "0.15rem 0 0", fontSize: "0.82rem", color: "#6b7280" }}>
                              {b.top_drug.brand_name}
                            </p>
                          )}
                        </div>
                        <span style={{
                          background: "#0284c7",
                          color: "white",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "20px",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                        }}>
                          Qty: {b.top_drug.total_quantity}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Low stock */}
                  {b.low_stock_drugs?.length > 0 && (
                    <div style={{ marginBottom: "1.25rem" }}>
                      <h4 style={{ marginBottom: "0.5rem", color: "#374151", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FaExclamationTriangle style={{ color: "#f59e0b" }} /> Low Stock ({b.low_stock_drugs.length})
                      </h4>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        {b.low_stock_drugs.map((drug, i) => (
                          <span
                            key={i}
                            style={{
                              background: "#fef3c7",
                              border: "1px solid #fcd34d",
                              borderRadius: "6px",
                              padding: "0.3rem 0.65rem",
                              fontSize: "0.82rem",
                              color: "#92400e",
                              fontWeight: 500,
                            }}
                          >
                            {drug.generic_name} — Qty: {drug.quantity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expiring drugs */}
                  {b.expiring_drugs?.length > 0 && (
                    <div>
                      <h4 style={{ marginBottom: "0.5rem", color: "#374151", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FaClock style={{ color: "#ef4444" }} /> Expiring Soon ({b.expiring_drugs.length})
                      </h4>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                          <thead>
                            <tr style={{ background: "#fef2f2" }}>
                              <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", color: "#374151", fontWeight: 600, borderBottom: "1px solid #fecaca" }}>Drug</th>
                              <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", color: "#374151", fontWeight: 600, borderBottom: "1px solid #fecaca" }}>Brand</th>
                              <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", color: "#374151", fontWeight: 600, borderBottom: "1px solid #fecaca" }}>Expiry</th>
                              <th style={{ padding: "0.5rem 0.75rem", textAlign: "right", color: "#374151", fontWeight: 600, borderBottom: "1px solid #fecaca" }}>Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {b.expiring_drugs.map((drug, i) => (
                              <tr key={i} style={{ borderBottom: "1px solid #fee2e2" }}>
                                <td style={{ padding: "0.5rem 0.75rem", color: "#1f2937" }}>{drug.generic_name}</td>
                                <td style={{ padding: "0.5rem 0.75rem", color: "#6b7280" }}>{drug.brand_name || "—"}</td>
                                <td style={{ padding: "0.5rem 0.75rem", color: "#dc2626", fontWeight: 500 }}>
                                  {drug.expiration_date
                                    ? new Date(drug.expiration_date).toLocaleDateString()
                                    : "—"}
                                </td>
                                <td style={{ padding: "0.5rem 0.75rem", textAlign: "right", color: "#1f2937" }}>{drug.quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* No alerts */}
                  {!b.low_stock_drugs?.length && !b.expiring_drugs?.length && (
                    <div style={{
                      background: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: "8px",
                      padding: "0.75rem 1rem",
                      color: "#166534",
                      fontSize: "0.9rem",
                    }}>
                      ✅ No low stock or expiring drugs in this branch.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── No branches message ── */}
        {branchAnalytics && branchAnalytics.branches?.length === 0 && (
          <div style={{
            marginTop: "2rem",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1.5rem",
            textAlign: "center",
            color: "#6b7280",
          }}>
            No branches found for branch analytics.
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;